import type { ProductIconMap } from '@/global.d.ts';

import { cookie, productEntitlements, productWithIcon } from '@/config.ts';

/**
 * Add URL parameters to an existing URL
 * @param url existing URL
 * @param params search parameters to append
 * @returns new URL with appended parameters
 */
export function addUrlParams(url: string, params: Record<string, string>): string {
	if (!url) {
		return '';
	}

	if (!params) {
		return url;
	}

	const givenUrl = new URL(url);
	const searchParams = new URLSearchParams(givenUrl.search);

	if (searchParams) {
		Object.entries(params).forEach(([key, value]) => {
			searchParams.append(key, value);
		});
	}

	return `${givenUrl.protocol}//${givenUrl.host}${givenUrl.pathname}?${searchParams.toString()}${givenUrl.hash}`;
}

/**
 * Retrieves the edition of the page either from a pageConfig or the URL. Falls back to 'at' if no edition is found.
 * @param dstWindow
 * @returns
 * @deprecated This will be removed once dst-components and dst-noticications are merged (PCM-149)
 */
export function getEdition(dstWindow: Window = window): 'at' | 'de' {
	return dstWindow.DERSTANDARD?.pageConfig?.edition ?? (dstWindow.location.origin.endsWith('.de') ? 'de' : 'at');
}

/**
 * Retrieves the MGUID from cookie with vanilla JS
 * @returns mguid
 */
export function getMguidFromCookie(): string {
	const value = `; ${document.cookie}`;
	const parts = value.split(`; ${cookie.mguid}=`);
	const mguid = parts.length === 2 ? parts.pop().split(';').shift() : '';

	if (mguid) {
		const parsedMguidCookie = mguid.substring(
			mguid.indexOf('GUID=') + 5,
			mguid.indexOf('&') > 0 ? mguid.indexOf('&') : mguid.length,
		);
		const uuidRegex = /^[0-9A-F]{8}-[0-9A-F]{4}-4[0-9A-F]{3}-[89AB][0-9A-F]{3}-[0-9A-F]{12}$/i;

		if (uuidRegex.test(parsedMguidCookie)) {
			return parsedMguidCookie;
		}
	}

	return '';
}

/**
 * Maps entitlements to products with icons
 * @param ent
 * @returns
 */
export function mapEntitlementsToProducts(ent: string[]): ProductIconMap[] {
	let products: string[] | { icon: string; name: string }[] = removePurEntitlementIfSmart(
		ent.filter(isProductEntitlement),
	);

	// added this exception, because there simply shouldn't be a way in which the default case is reached

	products = products.map((product) => {
		/* eslint-disable */
		switch (product) {
			case 'pur':
				return productWithIcon.pur;

			case 'sco':
			case 'sne':
				return productWithIcon.smart;

			case 'sup':
				return productWithIcon.supporter;

			default:
		}
		/* eslint-enable */
	});

	// removing any duplicates
	return [...new Set(products)];
}

/**
 * Remove the new notifications indicator from the bell icon
 * @returns
 */
export function removeNewNotificationsIndicator(): void {
	return window.postMessage(
		{
			eventName: 'notifications:hasViewed',
		},
		window.location.origin,
	);
}

/**
 * Returns whether the entitlement is a product entitlement
 * @param ent
 * @returns
 */
function isProductEntitlement(ent: string) {
	return productEntitlements.includes(ent);
}

/**
 * Returns an array of entitlements without the pur entitlement if the user has smart
 * @param ent
 * @returns
 */
function removePurEntitlementIfSmart(ent: string[]) {
	return ent.includes('sco') && ent.includes('pur') ? ent.filter((x) => x !== 'pur') : ent;
}
